// Smart back button that ensures correct filter state restoration
const SmartBackButton = {
    init() {
        // Only initialize back button on product pages
        if (window.location.pathname.includes('/products/')) {
            this.setupBackButton();
            console.log('SmartBackButton: Initializing on product page:', window.location.pathname);
        } else {
            console.log('SmartBackButton: Skipping back button setup, not on product page:', window.location.pathname);
        }
        this.trackCollectionState();
    },

    setupBackButton() {
        const maxRetries = 10; // Retry for 1 second
        let retryCount = 0;

        const trySetupButton = () => {
            const backButton = document.querySelector('.collection-return-button');
            if (backButton) {
                backButton.addEventListener('click', this.handleBackClick.bind(this));
                console.log('SmartBackButton: Back button initialized on', window.location.pathname);
            } else if (retryCount < maxRetries) {
                console.warn('SmartBackButton: Back button (.collection-return-button) not found, retrying... (Attempt:', retryCount + 1, ')');
                retryCount++;
                setTimeout(trySetupButton, 100);
            } else {
                console.error('SmartBackButton: Back button (.collection-return-button) not found after retries on', window.location.pathname);
            }
        };

        trySetupButton();
    },

    trackCollectionState() {
        const referrer = document.referrer;
        if (referrer && referrer.includes('/collections/')) {
            sessionStorage.setItem('collectionReferrer', referrer);
            const filterState = localStorage.getItem('filterState');
            if (filterState) {
                sessionStorage.setItem('expectedFilterState', filterState);
                console.log('SmartBackButton: Stored collection referrer:', referrer);
                console.log('SmartBackButton: Stored expected filter state:', JSON.parse(filterState));
            } else {
                console.warn('SmartBackButton: No filter state found in localStorage');
            }
        } else {
            console.log('SmartBackButton: Referrer not from collection page:', referrer);
        }
    },

    handleBackClick(e) {
        e.preventDefault();
        console.log('SmartBackButton: Back button clicked on', window.location.pathname, 'at', new Date().toLocaleString());
        
        const collectionReferrer = sessionStorage.getItem('collectionReferrer');
        const expectedFilterState = sessionStorage.getItem('expectedFilterState');
        
        if (window.history.length > 1 && collectionReferrer) {
            console.log('SmartBackButton: Using browser history.back() with filter validation');
            if (expectedFilterState) {
                this.setupReturnValidation(expectedFilterState);
            } else {
                console.warn('SmartBackButton: No expected filter state found');
            }
            window.history.back();
        } else {
            console.log('SmartBackButton: No history or referrer, using fallback navigation');
            this.navigateToFilteredCollection(collectionReferrer, expectedFilterState);
        }
    },

    setupReturnValidation(expectedFilterState) {
        const maxRetries = 100; // ~10 seconds
        let retryCount = 0;
        const timeout = 15000; // 15-second timeout

        const handlePageShow = async (event) => {
            console.log('SmartBackButton: Page shown at', new Date().toLocaleString(), 'Cached:', event.persisted);
            
            if (!window.FilterSystem || !window.FilterSystem.elements.app) {
                console.log('SmartBackButton: FilterSystem not initialized, initializing now');
                try {
                    await window.FilterSystem.init();
                } catch (error) {
                    console.error('SmartBackButton: Failed to initialize FilterSystem:', error);
                    this.navigateToFilteredCollection(
                        sessionStorage.getItem('collectionReferrer'),
                        expectedFilterState
                    );
                    return;
                }
            }

            const startTime = Date.now();
            const waitForProducts = () => {
                if (window.FilterSystem.allProducts.length > 0) {
                    console.log('SmartBackButton: Products loaded, validating filter state. Total products:', window.FilterSystem.allProducts.length);
                    const currentState = JSON.stringify(window.FilterSystem.state || {});
                    const expectedStateParsed = JSON.parse(expectedFilterState);
                    
                    console.log('SmartBackButton: Current FilterSystem state:', window.FilterSystem.state);
                    console.log('SmartBackButton: Expected state:', expectedStateParsed);
                    
                    if (currentState !== expectedFilterState) {
                        console.log('SmartBackButton: Filter states don’t match, restoring expected state');
                        this.restoreFilterState(expectedStateParsed);
                    } else {
                        console.log('SmartBackButton: Filter states match, forcing filter reapplication');
                        try {
                            window.FilterSystem.filterProducts();
                            window.FilterSystem.updatePriceRange();
                            window.FilterSystem.updateFilterCount();
                            window.FilterSystem.updateActiveFilters();
                            window.FilterSystem.displayProductsForPage(window.FilterSystem.currentPage || 1);
                            console.log('SmartBackButton: Displayed products for page:', window.FilterSystem.currentPage, 'Filtered products:', window.FilterSystem.filteredProducts.length);
                        } catch (error) {
                            console.error('SmartBackButton: Error forcing filter reapplication:', error);
                        }
                    }
                } else if (retryCount < maxRetries && (Date.now() - startTime) < timeout) {
                    console.warn('SmartBackButton: Products not loaded, retrying... (Attempt:', retryCount + 1, ')');
                    retryCount++;
                    setTimeout(waitForProducts, 100);
                } else {
                    console.error('SmartBackButton: Product loading timed out or max retries reached');
                    this.navigateToFilteredCollection(
                        sessionStorage.getItem('collectionReferrer'),
                        expectedFilterState
                    );
                }
            };
            
            requestAnimationFrame(waitForProducts);
            window.removeEventListener('pageshow', handlePageShow);
        };
        
        window.addEventListener('pageshow', handlePageShow, { once: true });
    },

    restoreFilterState(expectedState) {
        if (!window.FilterSystem) {
            console.error('SmartBackButton: FilterSystem not available for state restoration');
            return;
        }
        
        console.log('SmartBackButton: Restoring filter state at', new Date().toLocaleString(), ':', expectedState);
        
        window.FilterSystem.state = window.FilterSystem.state || {};
        const defaultState = {
            search: '',
            hideOutOfStock: false,
            priceMin: window.FilterSystem.config.defaultMinPrice,
            priceMax: window.FilterSystem.config.defaultMaxPrice,
            categories: [],
            occasions: [],
            recipient: [],
            productTypes: [],
            colors: [],
            sizes: [],
            vendors: [],
            availabilities: [],
            currentPage: 1
        };
        Object.assign(window.FilterSystem.state, defaultState, expectedState);
        window.FilterSystem.currentPage = expectedState.currentPage || 1;
        
        localStorage.setItem('filterState', JSON.stringify({
            ...window.FilterSystem.state,
            currentPage: window.FilterSystem.currentPage
        }));
        
        this.updateUIElements(window.FilterSystem.state);
        
        const maxRetries = 100;
        let retryCount = 0;
        const timeout = 15000;
        const startTime = Date.now();
        
        const applyFilters = () => {
            if (window.FilterSystem.allProducts.length > 0) {
                console.log('SmartBackButton: Products loaded, applying restored filters. Total products:', window.FilterSystem.allProducts.length);
                try {
                    window.FilterSystem.filterProducts();
                    console.log('SmartBackButton: FilterSystem.filterProducts called successfully. Filtered products:', window.FilterSystem.filteredProducts.length);
                    window.FilterSystem.updatePriceRange();
                    window.FilterSystem.updateFilterCount();
                    window.FilterSystem.updateActiveFilters();
                    window.FilterSystem.displayProductsForPage(window.FilterSystem.currentPage);
                    console.log('SmartBackButton: Displayed products for page:', window.FilterSystem.currentPage);
                } catch (error) {
                    console.error('SmartBackButton: Error applying filters:', error);
                }
            } else if (retryCount < maxRetries && (Date.now() - startTime) < timeout) {
                console.warn('SmartBackButton: Products not loaded, retrying... (Attempt:', retryCount + 1, ')');
                retryCount++;
                setTimeout(applyFilters, 100);
            } else {
                console.error('SmartBackButton: Product loading timed out or max retries reached in restoreFilterState');
                window.FilterSystem.loadAllProducts().then(() => {
                    window.FilterSystem.filterProducts();
                    window.FilterSystem.displayProductsForPage(window.FilterSystem.currentPage);
                });
            }
        };
        
        requestAnimationFrame(applyFilters);
    },

    updateUIElements(state) {
        if (!window.FilterSystem || !window.FilterSystem.elements) {
            console.warn('SmartBackButton: FilterSystem or elements not available for UI update');
            return;
        }
        
        const elements = window.FilterSystem.elements;
        console.log('SmartBackButton: Updating UI elements with state:', state);
        
        if (elements.searchInputs) {
            elements.searchInputs.forEach(input => {
                input.value = state.search || '';
                console.log(`SmartBackButton: Set search input to: ${state.search || ''}`);
            });
        }
        
        if (elements.hideOutOfStockCheckbox) {
            elements.hideOutOfStockCheckbox.checked = !!state.hideOutOfStock;
            console.log(`SmartBackButton: Set hideOutOfStock checkbox to: ${state.hideOutOfStock}`);
        }
        
        if (elements.priceMinRange && state.priceMin !== undefined) {
            elements.priceMinRange.value = state.priceMin;
            console.log(`SmartBackButton: Set priceMinRange to: ${state.priceMin}`);
        }
        if (elements.priceMaxRange && state.priceMax !== undefined) {
            elements.priceMaxRange.value = state.priceMax;
            console.log(`SmartBackButton: Set priceMaxRange to: ${state.priceMax}`);
        }
        
        if (elements.filterOptions) {
            elements.filterOptions.forEach(option => {
                option.checked = false;
                console.log(`SmartBackButton: Unchecked ${option.getAttribute('data-filter-type')}: ${option.getAttribute('data-filter-value')}`);
            });
            
            const filterMappings = {
                categories: 'category',
                occasions: 'occasion',
                recipient: 'recipient',
                productTypes: 'product_type',
                colors: 'color',
                sizes: 'size',
                vendors: 'vendor',
                availabilities: 'availability'
            };
            
            Object.entries(filterMappings).forEach(([stateKey, filterType]) => {
                if (state[stateKey] && state[stateKey].length > 0) {
                    state[stateKey].forEach(value => {
                        const checkbox = document.querySelector(`input[data-filter-type="${filterType}"][data-filter-value="${value}"]`);
                        if (checkbox) {
                            checkbox.checked = true;
                            console.log(`SmartBackButton: Checked ${filterType}: ${value}`);
                        } else {
                            console.warn(`SmartBackButton: Checkbox not found for ${filterType}: ${value}`);
                        }
                    });
                }
            });
        }
        
        try {
            if (window.FilterSystem.updatePriceRange) {
                window.FilterSystem.updatePriceRange();
                console.log('SmartBackButton: Updated price range display');
            }
            if (window.FilterSystem.updateFilterCount) {
                window.FilterSystem.updateFilterCount();
                console.log('SmartBackButton: Updated filter count');
            }
            if (window.FilterSystem.updateActiveFilters) {
                window.FilterSystem.updateActiveFilters();
                console.log('SmartBackButton: Updated active filters');
            }
        } catch (error) {
            console.error('SmartBackButton: Error updating UI elements:', error);
        }
    },

    navigateToFilteredCollection(collectionUrl, filterState) {
        let targetUrl = collectionUrl || '/collections/all';
        
        if (filterState && window.FilterSystem && window.FilterSystem.config) {
            localStorage.setItem('filterState', filterState);
            const state = JSON.parse(filterState);
            const params = new URLSearchParams();
            
            if (state.search) params.set('search', state.search);
            if (state.hideOutOfStock) params.set('in_stock', 'true');
            if (state.priceMin !== window.FilterSystem.config.defaultMinPrice) params.set('min_price', state.priceMin);
            if (state.priceMax !== window.FilterSystem.config.defaultMaxPrice) params.set('max_price', state.priceMax);
            if (state.currentPage && state.currentPage > 1) params.set('page', state.currentPage);
            
            ['categories', 'occasions', 'recipient', 'productTypes', 'colors', 'sizes', 'vendors', 'availabilities'].forEach(key => {
                if (state[key] && state[key].length) {
                    const paramName = {
                        categories: 'category',
                        occasions: 'occasion',
                        recipient: 'recipient',
                        productTypes: 'product_type',
                        colors: 'color',
                        sizes: 'size',
                        vendors: 'vendor',
                        availabilities: 'availability'
                    }[key];
                    params.set(paramName, state[key].join(','));
                }
            });
            
            const queryString = params.toString();
            if (queryString) {
                targetUrl += `?${queryString}`;
            }
        } else {
            console.warn('SmartBackButton: FilterSystem or config not available, navigating without filter parameters');
        }
        
        console.log('SmartBackButton: Navigating to:', targetUrl);
        window.location.href = targetUrl;
    }
};

// Enhanced collection page tracking
const CollectionStateTracker = {
    init() {
        if (!document.getElementById('custom-gifts-app')) {
            console.log('CollectionStateTracker: Not on collection page, skipping initialization');
            return;
        }
        
        this.restoreOnLoad();
        this.trackProductNavigation();
    },

    async restoreOnLoad() {
        const filterState = localStorage.getItem('filterState');
        if (filterState) {
            console.log('CollectionStateTracker: Collection page loaded at', new Date().toLocaleString(), 'checking stored filter state');
            
            if (!window.FilterSystem || !window.FilterSystem.elements.app) {
                console.log('CollectionStateTracker: FilterSystem not initialized, initializing now');
                try {
                    await window.FilterSystem.init();
                } catch (error) {
                    console.error('CollectionStateTracker: Failed to initialize FilterSystem:', error);
                    return;
                }
            }
            
            const maxRetries = 100;
            let retryCount = 0;
            const timeout = 15000;
            const startTime = Date.now();
            
            const applyStoredState = () => {
                if (window.FilterSystem.allProducts.length > 0) {
                    const expectedState = JSON.parse(filterState);
                    const currentState = window.FilterSystem.state ? JSON.stringify(window.FilterSystem.state) : null;
                    
                    console.log('CollectionStateTracker: Current FilterSystem state:', window.FilterSystem.state);
                    console.log('CollectionStateTracker: Expected state:', expectedState);
                    
                    if (currentState !== filterState) {
                        console.log('CollectionStateTracker: Restoring filter state on page load');
                        SmartBackButton.restoreFilterState(expectedState);
                    } else {
                        console.log('CollectionStateTracker: Filter state already matches, forcing filter reapplication');
                        try {
                            window.FilterSystem.filterProducts();
                            window.FilterSystem.updatePriceRange();
                            window.FilterSystem.updateFilterCount();
                            window.FilterSystem.updateActiveFilters();
                            window.FilterSystem.displayProductsForPage(window.FilterSystem.currentPage || 1);
                            console.log('CollectionStateTracker: Displayed products for page:', window.FilterSystem.currentPage, 'Filtered products:', window.FilterSystem.filteredProducts.length);
                        } catch (error) {
                            console.error('CollectionStateTracker: Error forcing filter reapplication:', error);
                        }
                    }
                } else if (retryCount < maxRetries && (Date.now() - startTime) < timeout) {
                    console.warn('CollectionStateTracker: Products not loaded, retrying... (Attempt:', retryCount + 1, ')');
                    retryCount++;
                    setTimeout(applyStoredState, 100);
                } else {
                    console.error('CollectionStateTracker: Product loading timed out or max retries reached');
                    SmartBackButton.navigateToFilteredCollection(
                        window.location.href,
                        filterState
                    );
                }
            };
            
            requestAnimationFrame(applyStoredState);
        } else {
            console.log('CollectionStateTracker: No stored filter state found');
        }
    },

    trackProductNavigation() {
        document.addEventListener('click', (e) => {
            const productLink = e.target.closest('a[href*="/products/"]');
            if (productLink && window.FilterSystem) {
                console.log('CollectionStateTracker: Product link clicked, saving current filter state');
                
                try {
                    window.FilterSystem.saveFilterState();
                    sessionStorage.setItem('collectionReferrer', window.location.href);
                    sessionStorage.setItem('expectedFilterState', localStorage.getItem('filterState'));
                    console.log('CollectionStateTracker: Saved collection referrer:', window.location.href);
                    console.log('CollectionStateTracker: Saved filter state:', JSON.parse(localStorage.getItem('filterState')));
                } catch (error) {
                    console.error('CollectionStateTracker: Error saving filter state on product navigation:', error);
                }
            }
        });
    }
};

// Initialize everything
document.addEventListener('DOMContentLoaded', () => {
    console.log('Initializing SmartBackButton and CollectionStateTracker at', new Date().toLocaleString());
    SmartBackButton.init();
    
    if (document.getElementById('custom-gifts-app')) {
        CollectionStateTracker.init();
    }
});